// A data point is labelled if its groupLabel differs from the previous
// point's, or if it is the first point. When `grouped` is false, every
// point is its own group — today's "label every point" behavior.
export const computeLabelIndexes = (data, grouped) => {
    if (!grouped) {
        return data.map((_, i) => i);
    }
    const out = [];
    for (let i = 0; i < data.length; i += 1) {
        if (i === 0 || data[i].groupLabel !== data[i - 1].groupLabel) {
            out.push(i);
        }
    }
    return out;
};
// Compute the absolute x position of every data point.
// Rules:
//   - Each adjacent pair of labelled indexes spans `pointSpacing`.
//   - Intra-group (unlabelled) points split that span evenly.
//   - Trailing orphans span exactly one extra `pointSpacing` past the last
//     label, with the very last point landing on the next "ghost label"
//     position and intermediates splitting that span evenly.
//   - With fewer than 2 labels we fall back to uniform pointSpacing.
export const computePointXs = (data, labelIndexes, pointSpacing, 
// Scale applied to the trailing span — the distance from the last group
// label to the last data point in that group. 1 = the original full
// pointSpacing; <1 = the whole trailing group is compressed (final point
// lands closer to the last label, intermediate orphans split the shrunken
// span evenly).
trailingSpanFactor = 1) => {
    if (data.length === 0)
        return [];
    if (data.length === 1)
        return [0];
    if (labelIndexes.length < 2) {
        return data.map((_, i) => i * pointSpacing);
    }
    const pointXs = new Array(data.length);
    pointXs[0] = 0;
    for (let l = 0; l < labelIndexes.length - 1; l += 1) {
        const startIdx = labelIndexes[l];
        const endIdx = labelIndexes[l + 1];
        const gapCount = endIdx - startIdx;
        const intraSpacing = pointSpacing / gapCount;
        for (let i = startIdx + 1; i <= endIdx; i += 1) {
            pointXs[i] = pointXs[startIdx] + (i - startIdx) * intraSpacing;
        }
    }
    const lastLabel = labelIndexes[labelIndexes.length - 1];
    if (lastLabel < data.length - 1) {
        const tailGapCount = data.length - 1 - lastLabel;
        const tailSpacing = (pointSpacing * trailingSpanFactor) / tailGapCount;
        for (let i = lastLabel + 1; i < data.length; i += 1) {
            pointXs[i] = pointXs[i - 1] + tailSpacing;
        }
    }
    return pointXs;
};
export const buildChartPath = (pointYs, pointXs) => {
    if (pointYs.length === 0)
        return '';
    return pointYs
        .map((y, i) => `${i === 0 ? 'M' : 'L'} ${pointXs[i]} ${y}`)
        .join(' ');
};
// Decide which data-point indexes get an axis label, and whether each one
// should display its bucket's groupLabel or the per-point label.
//   - Every entry from `labelIndexes` (the bucket starts) uses the group
//     label, e.g. "Mar".
//   - If `showLastPointLabel` is true and the last data point isn't already
//     a bucket-start, append a forced entry for it using the per-point
//     label, e.g. "28 Mar". Keeps positioning untouched — `computePointXs`
//     still only sees the bucket-starts.
export const computeAxisLabels = (labelIndexes, dataLength, showLastPointLabel) => {
    const entries = labelIndexes.map((index) => ({
        index,
        useGroupLabel: true,
    }));
    if (!showLastPointLabel || dataLength === 0)
        return entries;
    const lastIdx = dataLength - 1;
    if (entries.length > 0 && entries[entries.length - 1].index === lastIdx) {
        return entries;
    }
    entries.push({ index: lastIdx, useGroupLabel: false });
    return entries;
};
